import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, ListTodo, User, ExternalLink, Clock, FileText, MessageSquare, Ship } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { API_URL } from "../config/api";


// Task source configurations
const TASK_SOURCES = {
  quote_request: { icon: FileText, color: 'bg-blue-500', label: 'Quote Request' },
  support: { icon: MessageSquare, color: 'bg-purple-500', label: 'Support Chat' },
  pf: { icon: Ship, color: 'bg-emerald-500', label: 'Position Folder' },
  manual: { icon: ListTodo, color: 'bg-slate-500', label: 'Manual Task' },
};

export function TaskAssignmentNotifications() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token || user?.user_type === 'customer') return;
    
    try {
      const response = await axios.get(`${API_URL}/staff-tasks/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newNotifications = Array.isArray(response.data) ? response.data : [];
      
      setHasFetched(true);
      
      // Only show popup if there are actual notifications
      if (newNotifications.length > 0) {
        setNotifications(newNotifications);
        if (!currentNotification) {
          setCurrentNotification(newNotifications[0]);
        }
      } else {
        // Clear notifications if API returns empty
        setNotifications([]);
        setCurrentNotification(null);
      }
    } catch (error) {
      // On error, clear state
      setHasFetched(true);
      setNotifications([]);
      setCurrentNotification(null);
    }
  }, [token, user, currentNotification]);

  useEffect(() => {
    fetchNotifications();
    
    // Poll every 30 seconds for new task notifications
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Dismiss notification
  const handleDismiss = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/staff-tasks/notifications/${notificationId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from list and show next if available
      const remaining = notifications.filter(n => n.id !== notificationId);
      setNotifications(remaining);
      setCurrentNotification(remaining.length > 0 ? remaining[0] : null);
    } catch (error) {
      toast.error('Failed to dismiss notification');
    }
  };

  // View task details
  const handleViewTask = async (notification) => {
    // Navigate based on source
    if (notification.source === 'quote_request' && notification.reference) {
      navigate(`/offers/pricing?quote=${notification.reference}`);
    } else if (notification.source === 'support' && notification.reference) {
      navigate(`/support-queue?session=${notification.reference}`);
    } else {
      navigate('/dashboard');
    }
    
    // Dismiss after viewing
    await handleDismiss(notification.id);
  };

  // Don't render until we've fetched and confirmed there's something to show
  if (!hasFetched || !currentNotification) return null;

  const sourceConfig = TASK_SOURCES[currentNotification.source] || TASK_SOURCES.manual;
  const SourceIcon = sourceConfig.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className={`${sourceConfig.color} px-6 py-4 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-lg">
                <SourceIcon className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">New Task Assigned</h3>
                <p className="text-sm opacity-90">{sourceConfig.label}</p>
              </div>
            </div>
            <button
              onClick={() => handleDismiss(currentNotification.id)}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <h4 className="font-semibold text-lg text-slate-900 mb-2">
              {currentNotification.title || 'Task Assigned'}
            </h4>
            <p className="text-slate-600">
              {currentNotification.message}
            </p>
          </div>

          {currentNotification.reference && (
            <div className="bg-slate-50 rounded-lg p-3 mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="text-sm text-slate-600">Reference: </span>
              <span className="text-sm font-medium text-slate-900">{currentNotification.reference}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-slate-500 mb-6">
            <Clock className="h-4 w-4" />
            <span>
              {currentNotification.created_at 
                ? new Date(currentNotification.created_at).toLocaleString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                : 'Just now'
              }
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={() => handleViewTask(currentNotification)}
              className="flex-1 gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              View Task
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDismiss(currentNotification.id)}
              className="flex-1"
            >
              Dismiss
            </Button>
          </div>

          {/* Remaining notifications indicator */}
          {notifications.length > 1 && (
            <p className="text-center text-sm text-slate-500 mt-4">
              +{notifications.length - 1} more notification{notifications.length > 2 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default TaskAssignmentNotifications;
